import { FC, HTMLAttributes, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Flex, Form, Input, notification, Radio, Select, Typography } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { CreateFormButtons, prepareCreateWebsiteData, useSourceSlice, useUpdateSource } from 'entities/Source';
import { EVerificationOpenForm, ISource } from 'entities/Source/model/types';
import { EOnboardingStates, EOnboardingSteps, EResultTypes, useOnboardingSlice } from 'features/Onboarding';
import { urlValidator } from 'shared/consts';
import { useCommonSlice } from 'shared/hooks';
import { EMainRoutes, EYmEvents, EYmEventsO1, EYmEventsO2, IBadRequestError } from 'shared/typings';
import { InputUrl } from 'shared/ui';
import { sendYmEvent } from 'shared/utils';

interface ICreateWebsiteSourceFormProps extends HTMLAttributes<HTMLDivElement> {
	isOnboarding?: boolean;
	source?: ISource;
}

export const CreateWebsiteSourceForm: FC<ICreateWebsiteSourceFormProps> = ({
	className,
	isOnboarding,
	source,
	...props
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [skipVerification, setSkipVerification] = useState(false);

	const { websiteSourceTypeOptions, countryOptions, contentThemesOptions } = useCommonSlice();

	const { setStep, setModalState, setResultType, setCreatedSource } = useOnboardingSlice();
	const { type, websiteValues, setWebsiteValues, clearValues } = useSourceSlice();

	const { addSource, updateSource, data, isLoading, isSuccess, error } = useUpdateSource();

	const validationSchema = Yup.object({
		name: Yup.string()
			.required(t('Это поле обязательно для заполнения'))
			.max(64, t('Количество символов не должно превышать {{characters}}', { characters: 64 })),
		url: Yup.string()
			.required(t('Это поле обязательно для заполнения'))
			.matches(urlValidator, t('Введите корректный URL'))
			.max(4096, t('Количество символов не должно превышать {{characters}}', { characters: 4096 })),
		platform_type: Yup.string().required(t('Это поле обязательно для заполнения')),
		countries: Yup.array().min(1, t('Это поле обязательно для заполнения')),
		content_themes: Yup.array().min(1, t('Это поле обязательно для заполнения')),
		extension_description: Yup.string().when('extension_exists', (extension_exists: boolean[], schema) => {
			return extension_exists[0]
				? schema
						.required(t('Это поле обязательно для заполнения'))
						.max(2048, t('Количество символов не должно превышать {{characters}}', { characters: 2048 }))
				: schema;
		}),
	});

	useEffect(() => {
		if (!isSuccess) return;

		sendYmEvent({
			event: EYmEvents.SEND_ADD_ADSPACE_SUCCESS,
			o1: [EYmEventsO1.ACCOUNT, EYmEventsO1.PUBLISHER, isOnboarding ? EYmEventsO1.WELCOME_POPUP : EYmEventsO1.ADSPACE],
			o2: EYmEventsO2.ADD_ADSPACE,
			o4: type,
			wi: data?.uuid,
		});

		if (isOnboarding) {
			setCreatedSource(data || null);

			if (skipVerification) {
				setResultType(EResultTypes.NEED_VERIFICATION);
				setModalState(EOnboardingStates.FINAL);
			} else {
				setStep(EOnboardingSteps.THIRD);
			}
		} else {
			clearValues();

			navigate(
				skipVerification
					? `/${EMainRoutes.SOURCES}/`
					: `/${EMainRoutes.SOURCES}/${EMainRoutes.VERIFICATION}/${data?.uuid}?open=${EVerificationOpenForm.CREATE}`,
			);

			skipVerification &&
				notification.open({
					message: t('Требуется подтвердить владение площадкой'),
					description: t(
						'{{sourceName}} создана успешно, но требуется подтвердить ее владение. Без подтверждения ее владения и прохождения модерации, вы не сможете подавать заявки на подключение к офферам.',
						{ sourceName: data?.name },
					),
					type: 'warning',
					placement: 'topRight',
					showProgress: true,
					pauseOnHover: true,
				});
		}
	}, [isSuccess]); // eslint-disable-line

	return (
		<Formik
			initialValues={websiteValues}
			validationSchema={validationSchema}
			onSubmit={(values) => {
				source?.uuid
					? updateSource({ ...prepareCreateWebsiteData(values, type), uuid: source.uuid })
					: addSource(prepareCreateWebsiteData(values, type));
			}}>
			{({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
				return (
					<form onSubmit={handleSubmit}>
						<Flex {...props} vertical gap={24} className={className}>
							<Flex vertical gap={20}>
								<Form.Item
									layout="vertical"
									label={t('Название площадки')}
									required
									validateStatus={errors.name && touched.name ? 'error' : ''}
									help={errors.name && touched.name && errors.name}>
									<Input
										name="name"
										placeholder="Укажите название"
										onChange={(e) => {
											setWebsiteValues({ ...websiteValues, name: e.target.value });
											handleChange(e);
										}}
										onBlur={handleBlur}
										value={values.name}
										disabled={isLoading}
									/>
								</Form.Item>

								<Form.Item
									layout="vertical"
									label={t('URL площадки')}
									required
									validateStatus={errors.url && touched.url ? 'error' : ''}
									help={errors.url && touched.url && errors.url}>
									<InputUrl
										name="url"
										placeholder="Укажите URL"
										onChange={(e) => {
											setWebsiteValues({ ...websiteValues, url: e.target.value });
											handleChange(e);
										}}
										onBlur={handleBlur}
										value={values.url}
										disabled={isLoading}
										errors={(error as IBadRequestError)?.data?.website_data?.url}
									/>
								</Form.Item>

								<Form.Item
									layout="vertical"
									label={t('Тип платформы')}
									required
									validateStatus={errors.platform_type && touched.platform_type ? 'error' : ''}
									help={errors.platform_type && touched.platform_type && errors.platform_type}>
									<Select
										options={websiteSourceTypeOptions}
										onChange={(e) => {
											setWebsiteValues({ ...websiteValues, platform_type: e });
											setFieldValue('platform_type', e);
										}}
										onBlur={handleBlur}
										value={values.platform_type}
										showSearch
										optionFilterProp="label"
										placeholder={t('Выберите тип платформы')}
										allowClear
										disabled={isLoading}
									/>
								</Form.Item>

								<Form.Item
									layout="vertical"
									label={`${t('Страна аудитории')} (${t('Макс.')} 5)`}
									required
									validateStatus={errors.countries && touched.countries ? 'error' : ''}
									help={errors.countries && touched.countries && errors.countries}>
									<Select
										options={countryOptions}
										onChange={(e) => {
											setWebsiteValues({ ...websiteValues, countries: e });
											setFieldValue('countries', e);
										}}
										onBlur={handleBlur}
										value={values.countries}
										mode="multiple"
										maxCount={5}
										maxTagCount="responsive"
										showSearch
										optionFilterProp="label"
										placeholder={t('Выберите локали')}
										allowClear
										disabled={isLoading}
									/>
									<Typography.Text type="secondary" style={{ marginTop: 2 }}>
										{t('Помогите улучшить инструмент для поиска брендов')}
									</Typography.Text>
								</Form.Item>

								<Form.Item
									layout="vertical"
									label={`${t('Тематика контента')} (${t('Макс.')} 3)`}
									required
									validateStatus={errors.content_themes && touched.content_themes ? 'error' : ''}
									help={errors.content_themes && touched.content_themes && errors.content_themes}>
									<Select
										options={contentThemesOptions}
										onChange={(e) => {
											setWebsiteValues({ ...websiteValues, content_themes: e });
											setFieldValue('content_themes', e);
										}}
										onBlur={handleBlur}
										value={values.content_themes}
										mode="multiple"
										maxCount={3}
										maxTagCount="responsive"
										showSearch
										optionFilterProp="label"
										placeholder={t('Выберите тематики')}
										allowClear
										disabled={isLoading}
									/>
								</Form.Item>

								<Form.Item
									layout="vertical"
									label={t('У вас есть расширение?')}
									validateStatus={errors.extension_exists && touched.extension_exists ? 'error' : ''}
									help={errors.extension_exists && touched.extension_exists && errors.extension_exists}>
									<Radio.Group
										name="extension_exists"
										onChange={(e) => {
											setWebsiteValues({ ...values, extension_exists: e.target.value });
											handleChange(e);
										}}
										onBlur={handleBlur}
										value={values.extension_exists}
										disabled={isLoading}>
										<Radio key={'true'} value={true}>
											{t('Да')}
										</Radio>
										<Radio key={'false'} value={false}>
											{t('Нет')}
										</Radio>
									</Radio.Group>
								</Form.Item>

								{values.extension_exists && (
									<Form.Item
										layout="vertical"
										label={t('Пожалуйста, укажите ссылки и укажите ОСНОВНУЮ функциональность расширения.')}
										required
										validateStatus={errors.extension_description && touched.extension_description ? 'error' : ''}
										help={
											errors.extension_description && touched.extension_description && errors.extension_description
										}>
										<Input.TextArea
											name="extension_description"
											onChange={(e) => {
												setWebsiteValues({ ...websiteValues, extension_description: e.target.value });
												handleChange(e);
											}}
											onBlur={handleBlur}
											value={values.extension_description}
											disabled={isLoading}
											style={{ height: 100 }}
										/>
									</Form.Item>
								)}
							</Flex>

							<CreateFormButtons
								isOnboarding={isOnboarding}
								isLoading={isLoading}
								errors={errors}
								touched={touched}
								setSkipVerification={setSkipVerification}
								isWebsite
							/>
						</Flex>
					</form>
				);
			}}
		</Formik>
	);
};
